import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AthenaLogo } from './AthenaLogo';

const QUESTIONS = [
  {
    id: 'purpose',
    q: 'O que trouxe você até mim hoje?',
    opts: ['Trabalho e produtividade', 'Criatividade e projetos', 'Aprender e explorar'],
  },
  {
    id: 'ai_usage',
    q: 'Como você costuma usar IA?',
    opts: ['Estou começando agora', 'Uso às vezes para tarefas', 'Uso diariamente como ferramenta'],
  },
  {
    id: 'priority',
    q: 'O que é mais importante para você?',
    opts: ['Respostas rápidas e diretas', 'Análises profundas e detalhadas', 'Criatividade e novas ideias'],
  },
  {
    id: 'style',
    q: 'Como você prefere que eu converse com você?',
    opts: ['Formal e profissional', 'Casual e direto', 'Como parceiro de pensamento'],
  },
  {
    id: 'about',
    q: 'Existe algo que eu deveria saber sobre você?',
    opts: ['Sou empreendedor / gestor', 'Sou criativo / designer', 'Sou técnico / desenvolvedor'],
  },
];

const C = {
  bg: '#030712', card: '#0c1220', border: '#1a2240',
  accent: '#3B82F6', accentD: '#1e3a5f', accentB: '#0a1628',
  text: '#e8eaf2', muted: '#6b7590', dim: '#2e3560',
};

export function OnboardingModal({ onComplete, onSkip }) {
  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState({});
  const [custom, setCustom]   = useState('');
  const [selected, setSelected] = useState(null);

  const q = QUESTIONS[step];
  const isLast = step === QUESTIONS.length - 1;

  const choose = (opt) => {
    setSelected(opt);
    setCustom('');
  };

  const next = () => {
    const answer = custom.trim() || selected;
    if (!answer) return;
    const updated = { ...answers, [q.id]: answer };
    setAnswers(updated);
    if (isLast) {
      localStorage.setItem('athena_onboarding_done', '1');
      localStorage.setItem('athena_onboarding_profile', JSON.stringify(updated));
      onComplete(updated);
    } else {
      setStep(s => s + 1);
      setSelected(null);
      setCustom('');
    }
  };

  const skip = () => {
    localStorage.setItem('athena_onboarding_done', '1');
    onSkip();
  };

  const canAdvance = !!(custom.trim() || selected);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(3,7,18,0.84)', backdropFilter: 'blur(14px)',
      padding: '0 20px', boxSizing: 'border-box',
    }}>
      {/* Orb ambient glow */}
      <div style={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
        pointerEvents: 'none', top: '10%', left: '50%', transform: 'translateX(-50%)',
      }} />

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.28 }}
          style={{
            width: '100%', maxWidth: 440,
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 20,
            boxShadow: '0 32px 80px rgba(0,0,0,0.8)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Progress bar */}
          <div style={{ height: 2, background: C.border }}>
            <div style={{
              height: '100%',
              width: `${((step + 1) / QUESTIONS.length) * 100}%`,
              background: C.accent,
              transition: 'width 0.4s ease',
            }} />
          </div>

          <div style={{ padding: '28px 28px 24px' }}>
            {/* Logo — só no primeiro passo */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <AthenaLogo size={72} variant="vertical" showText={true} />
            </div>

            {/* Step counter + skip */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <span style={{ fontSize: 11, color: C.dim, fontFamily: 'monospace', letterSpacing: '.08em' }}>
                {step + 1} / {QUESTIONS.length}
              </span>
              <button onClick={skip} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: C.muted, fontSize: 12, padding: '4px 8px',
              }}
                onMouseEnter={e => e.currentTarget.style.color = C.text}
                onMouseLeave={e => e.currentTarget.style.color = C.muted}
              >
                Pular →
              </button>
            </div>

            {/* Question */}
            <h2 style={{ fontSize: 18, fontWeight: 500, color: C.text, margin: '0 0 20px', lineHeight: 1.4 }}>
              {q.q}
            </h2>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {q.opts.map(opt => (
                <button key={opt} onClick={() => choose(opt)} style={{
                  padding: '11px 16px',
                  borderRadius: 10,
                  border: `1px solid ${selected === opt ? C.accent : C.border}`,
                  background: selected === opt ? C.accentB : 'rgba(255,255,255,0.02)',
                  color: selected === opt ? C.accent : C.text,
                  fontSize: 13, cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => { if (selected !== opt) { e.currentTarget.style.borderColor = C.accentD; e.currentTarget.style.background = 'rgba(59,130,246,0.04)'; } }}
                  onMouseLeave={e => { if (selected !== opt) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; } }}
                >
                  {opt}
                </button>
              ))}
            </div>

            {/* Custom text */}
            <input
              value={custom}
              onChange={e => { setCustom(e.target.value); setSelected(null); }}
              placeholder="Escrever minha resposta..."
              style={{
                width: '100%', padding: '10px 14px',
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${custom ? C.accent : C.border}`,
                borderRadius: 10, color: C.text, fontSize: 13,
                outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => e.target.style.borderColor = C.accent}
              onBlur={e => e.target.style.borderColor = custom ? C.accent : C.border}
            />

            {/* Next */}
            <button
              onClick={next}
              disabled={!canAdvance}
              style={{
                width: '100%', padding: '13px', marginTop: 16,
                borderRadius: 12, fontSize: 13, fontWeight: 500, cursor: canAdvance ? 'pointer' : 'default',
                background: canAdvance ? C.accent : C.accentB,
                border: `1px solid ${canAdvance ? C.accent : C.accentD}`,
                color: canAdvance ? '#fff' : C.muted,
                transition: 'all 0.2s',
              }}
            >
              {isLast ? 'Começar com Athena' : 'Continuar →'}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
